// Mirror of the bill-of-lading sanitizer, sanitizeBillOfLadingString, in
// backend/platform/src/main/kotlin/net/freightsimple/platform/pdf/acroFormExtensions.kt.
// That Kotlin function is the source of truth for what prints on a BOL: every
// explicit replacement, the Cyrillic look-alike map, the \p{Z} separator pass and
// unaccent() are reproduced here so the customer Dashboard warns only on
// characters that would still print as "?". A mapping added to or removed from
// the Kotlin sanitizer must be made here too, or the Dashboard drifts: it nags a
// customer about a character that now prints fine, or stays quiet about one that
// no longer does.
const BILL_OF_LADING_MAPPED_CHARACTERS = new Set<string>([
  "\uFF0C",
  "\u200B",
  "\u3001",
  "\uFF08",
  "\uFF09",
  "\uFF1A",
  "\u25CF",
  "\u2105",
  "\u2212",
  "\u2043",
  "\u00AD",
  "\u2019",
  "\u2018",
  "\u2033",
  "\u2032",
  "\u201C",
  "\u201D",
  "\u2022",
  "\u00B0",
  "\u00A9",
  "\u00B9",
  "\u00B2",
  "\u00B3",
  "\u00D7",
  "\u2122",
  "\u00AE",
  ..."\u0410\u0412\u0415\u0417\u041A\u041C\u041D\u041E\u0420\u0421\u0422\u0423\u0425",
  ..."\u0430\u0432\u0435\u043A\u043C\u043D\u043E\u0440\u0441\u0442\u0443\u0445",
  ..."\u0456\u0406\u0458\u0408",
]);

const UNICODE_SEPARATOR = /\p{Z}/u;
const UNICODE_DASH = /\p{Pd}/u;
const COMBINING_DIACRITICAL_MARKS = /[\u0300-\u036F]/g;

function isAscii(text: string): boolean {
  return [...text].every((character) => character.codePointAt(0)! < 0x80);
}

function printsOnBillOfLading(character: string): boolean {
  if (isAscii(character)) return true;
  if (UNICODE_SEPARATOR.test(character)) return true;
  if (UNICODE_DASH.test(character)) return true;
  if (BILL_OF_LADING_MAPPED_CHARACTERS.has(character)) return true;
  const unaccented = character
    .normalize("NFD")
    .replace(COMBINING_DIACRITICAL_MARKS, "");
  return unaccented.length > 0 && isAscii(unaccented);
}

export function unprintableBillOfLadingCharacters(
  text: string | undefined,
): string[] {
  if (!text) return [];
  return [...new Set([...text].filter((c) => !printsOnBillOfLading(c)))];
}

export function hasUnprintableBillOfLadingCharacters(
  text: string | undefined,
): boolean {
  return unprintableBillOfLadingCharacters(text).length > 0;
}
